import { Tobacco } from '../lib/supabase';
import { Leaf, Plus, Check, Star } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useEmberStore } from '../lib/store';
import { toast } from '../lib/toast';
import Button from './ui/Button';

interface TobaccoCardProps {
  tobacco: Tobacco;
}

export default function TobaccoCard({ tobacco }: TobaccoCardProps) {
  const {
    name,
    brand,
    type,
    flavor_tags,
    strength,
    origin,
    cut,
    avg_rating,
    review_count,
  } = tobacco;
  const { currentUser } = useEmberStore();
  const [adding, setAdding] = useState(false);
  const [inWishList, setInWishList] = useState(false);

  const tags = Array.isArray(flavor_tags) ? flavor_tags : [];
  const numericRating = useMemo(() => {
    if (typeof avg_rating === 'number') {
      return Number.isNaN(avg_rating) ? null : avg_rating;
    }
    if (avg_rating != null) {
      const parsed = Number(avg_rating);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  }, [avg_rating]);
  const numericReviewCount = useMemo(() => {
    if (typeof review_count === 'number') {
      return Number.isNaN(review_count) ? null : review_count;
    }
    if (review_count != null) {
      const parsed = Number(review_count);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  }, [review_count]);
  const hasRating = numericRating != null;

  // Check if already in wish list for current user
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!currentUser) return;
        // First try by reference id
        const { data: byRef } = await supabase
          .from('wish_tobacco')
          .select('id')
          .eq('user_id', currentUser.id)
          .eq('blend_ref_id', tobacco.id)
          .limit(1);
        if (byRef && byRef.length > 0) {
          if (!cancelled) setInWishList(true);
          return;
        }
        // Fallback: match by brand + blend text (case-insensitive)
        const { data: byText } = await supabase
          .from('wish_tobacco')
          .select('id')
          .eq('user_id', currentUser.id)
          .ilike('brand', brand || '')
          .ilike('blend_name', name)
          .limit(1);
        if (!cancelled) setInWishList((byText || []).length > 0);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [currentUser, tobacco.id, brand, name]);

  async function handleAddToWish() {
    if (!currentUser) {
      toast.info('Please log in to add to your wish list');
      return;
    }
    if (inWishList) return;
    setAdding(true);
    try {
      // Attempt insert including reference id first
      const payloadBase = {
        user_id: currentUser.id,
        brand: brand || 'Unknown',
        blend_name: name,
        created_at: new Date().toISOString(),
      } as any;

      let { error } = await supabase.from('wish_tobacco').insert({
        ...payloadBase,
        blend_ref_id: tobacco.id,
      });

      if (error) {
        // 23503 = foreign key violation (blend_ref_id does not exist in reference table)
        // 23505 = unique violation (already exists)
        const code = (error as any).code;
        if (code === '23503') {
          // Retry without reference id, store text only
          const { error: retryErr } = await supabase.from('wish_tobacco').insert(payloadBase);
          if (retryErr) throw retryErr;
          setInWishList(true);
          toast.success('Added to your Wish List');
          return;
        }
        if (code === '23505') {
          setInWishList(true);
          toast.info('Already in your Wish List');
          return;
        }
        throw error;
      }
      setInWishList(true);
      toast.success('Added to your Wish List');
    } catch (err) {
      console.error('Add to wish list failed:', err);
      toast.error('Could not add to Wish List');
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className="card" style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: '1rem' }}>
        <div
          style={{
            width: '60px',
            height: '60px',
            backgroundColor: '#5a3310',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Leaf size={32} color="#ff9500" />
        </div>

        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: '1.125rem', marginBottom: '0.25rem' }}>
            {name}
          </h3>
          <p style={{ fontSize: '0.875rem', color: '#d3de5e', marginBottom: '0.5rem' }}>
            {brand} • {type}
          </p>

          {/* Flavor Tags */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
            {tags.map((tag) => (
              <span
                key={tag}
                style={{
                  fontSize: '0.75rem',
                  padding: '0.25rem 0.5rem',
                  backgroundColor: '#5a3310',
                  borderRadius: '4px',
                  color: '#d3de5e',
                }}
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Strength & Origin */}
          <div style={{ fontSize: '0.75rem', color: '#999' }}>
            {strength && <span>Strength: {strength}</span>}
            {strength && origin && <span> • </span>}
            {origin && <span>{origin}</span>}
          </div>

          {/* Cut */}
          {cut && (
            <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.25rem' }}>
              Cut: {cut}
            </div>
          )}

          {/* Rating */}
          {hasRating && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              fontSize: '0.75rem',
              color: '#d3de5e',
              marginTop: '0.5rem',
            }}>
              <Star size={14} color="#ff9500" fill="#ff9500" />
              <span>{numericRating!.toFixed(2)}</span>
              {numericReviewCount != null && numericReviewCount > 0 && (
                <span style={{ color: '#999' }}>({numericReviewCount} reviews)</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add to Wish List button - moved to bottom to avoid overlapping title */}
      <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'flex-end' }}>
        {inWishList ? (
          <Button variant="secondary" size="small" aria-label="In Wish List" disabled>
            <Check size={14} /> In Wish List
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="small"
            onClick={handleAddToWish}
            loading={adding}
            aria-label="Add to Wish List"
          >
            <Plus size={14} /> Add to Wish
          </Button>
        )}
      </div>
    </div>
  );
}

